import React, { useState, type ReactNode } from "react";
import { RxTriangleDown } from "react-icons/rx";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "../core/basic/Button";
import { Menu, MenuButton, MenuItem, Separator } from "../core/basic/Menu";
import { Modal, type Props as ModalProps } from "../core/basic/RACModal";

export type BulkActionOption<T> = ModalAction<T> | CustomAction<T> | BulkActionDivider;

interface ModalAction<T> {
  /** Needed if two options use same label, otherwise uses label as id */
  id?: string;
  label: string | ((selected: T[]) => string);
  action: (selected: T[]) => void | Promise<void>;
  confirmationMessage: ReactNode;
  actionFormFields?: ReactNode;
  disableConfirmButton?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  divider?: never;
}

interface CustomAction<T> {
  /** Needed if two options use same label, otherwise uses label as id */
  id?: string;
  label: string | ((selected: T[]) => string);
  action: (selected: T[]) => void | Promise<void>;
  hidden?: boolean;
  disabled?: boolean;

  divider?: never;
  confirmationMessage?: never;
  actionFormFields?: never;
  disableConfirmButton?: never;
}

interface BulkActionDivider {
  id?: never;
  label?: never;
  divider: true;
  hidden?: boolean;
}

interface Props<T> {
  selected?: T[];
  options?: BulkActionOption<T>[];
  hidden?: boolean;
}

export const BulkActionButton = <T,>({ selected = [], options = [], hidden }: Props<T>) => {
  const [openOptionId, setOpenOptionId] = useState<string | null>(null);
  if (hidden) {
    return null;
  }

  const openOption = options.find(({ id, divider, label }) => {
    if (divider) return false;
    const resolvedLabel = typeof label === "function" ? label(selected) : label;
    const resolvedId = id ?? resolvedLabel;
    return resolvedId === openOptionId;
  }) as ModalAction<T> | undefined;

  return (
    <>
      <MenuButton
        label={
          <>
            {`Bulk Actions (${selected.length} selected)`} <RxTriangleDown fontSize={"16px"} />
          </>
        }
        buttonProps={{
          isDisabled: selected.length === 0,
          fontSize: "small",
          fontWeight: "bold",
        }}
      >
        <Menu className={css({ width: "var(--trigger-width)" })}>
          {options
            .filter((option) => !option.hidden)
            .map((option, i) => {
              if (option.divider) {
                return <Separator key={`${i}-divider`} />;
              }

              const { id, label, action, confirmationMessage, disabled } = option;
              const resolvedLabel = typeof label === "function" ? label(selected) : label;
              const resolvedId = id ?? resolvedLabel;

              return (
                <MenuItem
                  key={resolvedId}
                  onAction={() => {
                    if (confirmationMessage) {
                      setOpenOptionId(resolvedId);
                    } else {
                      void action(selected);
                    }
                  }}
                  isDisabled={disabled === true}
                >
                  {resolvedLabel}
                </MenuItem>
              );
            })}
        </Menu>
      </MenuButton>
      {openOption && (
        <BulkActionModal
          isOpen={!!openOption}
          onOpenChange={(isOpen) => !isOpen && setOpenOptionId(null)}
          onConfirm={() => void openOption.action(selected)}
          disableConfirm={openOption.disableConfirmButton}
        >
          {openOption.actionFormFields}
          {openOption.confirmationMessage}
        </BulkActionModal>
      )}
    </>
  );
};

function BulkActionModal({
  children,
  onConfirm,
  disableConfirm,
  ...props
}: ModalProps & { onConfirm: () => void; disableConfirm?: boolean }) {
  return (
    <Modal
      title="Confirm Bulk Action"
      className={css({
        width: "60vw",
      })}
      {...props}
    >
      {(opts) => (
        <>
          <VStack gap={"medium"} alignItems={"center"} paddingX="large" overflowX={"hidden"} overflowY="auto">
            {typeof children === "function" ? children(opts) : children}
          </VStack>
          <HStack justifyContent={"space-evenly"} width="100%">
            <Button variant={"secondary"} onPress={() => opts.close()}>
              Cancel
            </Button>
            <Button
              onPress={() => {
                onConfirm();
                opts.close();
              }}
              isDisabled={disableConfirm}
            >
              Confirm
            </Button>
          </HStack>
        </>
      )}
    </Modal>
  );
}
